import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Leaf, ShieldCheck, GraduationCap, MapPin, Sparkle, SealCheck } from "@phosphor-icons/react";

export default function YouthPublicPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);      // by-code projection
  const [impact, setImpact] = useState(null);
  const [asset, setAsset] = useState("https://naturepulse.net");
  const [disc, setDisc] = useState(null);
  const [err, setErr] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    let alive = true;
    setErr(false);
    api.get(`/youth/by-code/${code}`).then((r) => { if (alive) setData(r.data.public); }).catch(() => { if (alive) setErr(true); });
    api.get(`/youth/by-code/${code}/impact`).then((r) => { if (alive) setImpact(r.data.impact); }).catch(() => {});
    api.get(`/youth/config`).then((r) => { if (alive) setAsset(r.data.asset_base); }).catch(() => {});
    api.get(`/youth/disclaimer`).then((r) => { if (alive) setDisc(r.data.disclaimer); }).catch(() => {});
    return () => { alive = false; };
  }, [code]);

  if (err) return <div className="p-16 text-center text-muted-foreground" data-testid="youth-not-found">This Youth Code isn't available.</div>;
  if (!data) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;

  const headUrl = data.avatar?.head_url ? `${asset}${data.avatar.head_url}` : null;
  const badges = data.badges || impact?.top_badges || [];
  const contributions = impact?.contribution_count ?? data.contribution_count ?? 0;

  return (
    <main className="mx-auto max-w-2xl px-5 py-10" data-testid="youth-public-page">
      <Card className="overflow-hidden border-border/60 shadow-sm">
        <div className="relative h-28 bg-[#1a7a4c] grain" />
        <div className="px-6 pb-8 sm:px-8">
          <div className="-mt-14 flex items-end gap-4">
            <div className="grid h-28 w-28 place-items-center overflow-hidden rounded-2xl border-4 border-card bg-secondary/15 shadow-md">
              {headUrl && !imgError ? <img src={headUrl} alt="silhouette" className="h-full w-full object-contain" onError={() => setImgError(true)} data-testid="youth-avatar" />
                       : <Leaf size={40} className="text-secondary" />}
            </div>
            <div className="mb-2 flex flex-wrap gap-2">
              {data.verified?.school && <Badge className="gap-1 rounded-full bg-[#1a7a4c] text-white" data-testid="youth-verified-school"><SealCheck size={13} weight="fill" /> School-verified</Badge>}
              {data.verified?.guardian && <Badge className="gap-1 rounded-full bg-secondary text-secondary-foreground" data-testid="youth-verified-guardian"><ShieldCheck size={13} weight="fill" /> Guardian-approved</Badge>}
            </div>
          </div>

          <div className="mt-4">
            <h1 className="font-heading text-3xl font-medium tracking-tight" data-testid="youth-name">{data.first_name}</h1>
            <p className="mt-1 text-lg text-secondary" data-testid="youth-role">{data.public_role}</p>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
              {data.school_name && <span className="flex items-center gap-1.5"><GraduationCap size={15} /> {data.school_name}{data.school_group ? ` · ${data.school_group}` : ""}</span>}
              {(data.region || data.country) && <span className="flex items-center gap-1.5"><MapPin size={15} /> {[data.region, data.country].filter(Boolean).join(", ")}</span>}
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">Ages {data.age_band?.replace("_", "–")}</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-muted/50 p-4">
              <div className="font-heading text-2xl font-medium text-[#1a7a4c]" data-testid="youth-contributions">{contributions}</div>
              <div className="text-xs text-muted-foreground">Observations</div>
            </div>
            <div className="rounded-xl bg-muted/50 p-4">
              <div className="font-heading text-2xl font-medium text-[#1a7a4c]">{badges.length}</div>
              <div className="text-xs text-muted-foreground">Badges earned</div>
            </div>
          </div>

          {badges.length > 0 && (
            <div className="mt-6">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-medium"><Sparkle size={18} className="text-secondary" weight="duotone" /> Nature badges</h2>
              <div className="flex flex-wrap gap-2" data-testid="youth-badges">
                {badges.map((b) => (
                  <span key={b.slug} className="flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-sm">
                    <span aria-hidden>{b.emoji}</span> {b.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      {disc && (
        <Card className="mt-5 border-[#1a7a4c]/25 bg-[#1a7a4c]/5 p-5" data-testid="youth-disclaimer">
          <div className="flex items-start gap-3">
            <ShieldCheck size={22} weight="duotone" className="mt-0.5 shrink-0 text-[#1a7a4c]" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#1a7a4c]">{disc.eyebrow}</p>
              <p className="font-heading text-base font-medium">{disc.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{disc.body}</p>
            </div>
          </div>
        </Card>
      )}
      <p className="mt-4 text-center text-xs text-muted-foreground">Youth Code {data.youth_code} · Powered by NaturePulse</p>
    </main>
  );
}
